/**
 * src/components/cliente/TiendasDelTenant.jsx
 * Dónde factura el tenant, tienda a tienda, y qué parte de eso vigilamos.
 *
 * Vive en «Cómo va» y no en Ejecuciones (Manuel, 01-09-2026): es una respuesta
 * sobre el NEGOCIO del tenant, no una herramienta de operación, y en Ejecuciones
 * se perdía entre las tarjetas de diagnóstico.
 *
 * UNA tabla, no dos. Manuel propuso añadir una segunda tabla con los volúmenes
 * por tienda; tendría las MISMAS filas que ésta, y dos tablas con las mismas
 * filas obligan a cruzarlas con el dedo. Además «Cómo va» está limitada a 960 px
 * a propósito. Lo valioso es justo el cruce: la fila que solo está en una de las
 * dos fuentes —dinero en una tienda sin familia— es cobertura que se pierde, y
 * en dos tablas separadas no se ve.
 */
import { useState } from 'react'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Table } from '../ui/Table'
import { Pista } from '../ui/Pista'
import { BarraTramos } from '../ui/BarraTramos'
import { rutaDeAccion } from './EstadoDeLaAccion'
import { useTiendas, useUpdateFamilia } from '../../hooks/useClientes'
import { useNombreDeTienda } from '../../hooks/useConvenios'
// `—` y no `$0` lo garantiza `lib/cifras` (§3). Estaba copiado aquí y en
// Ejecuciones, y las dos copias ya hacían cosas distintas ante un nulo.
import { clp } from '../../lib/cifras'

const ORIGEN_TEXTO = {
  wizard: 'del alta',
  auto:   'detectada en sus OCs',
  admin:  'fijada a mano',
}

// La etiqueta la decide el backend (una sola vez, con sus tests); aquí solo se
// pinta. Decidirla otra vez en el navegador serían dos reglas para lo mismo.
const ESTADO = {
  vigilada:          { color: 'green',  texto: 'la vigilamos' },
  sin_dinero_medido: { color: 'gray',   texto: 'sin dinero medido' },
  apagada:           { color: 'gray',   texto: 'apagada' },
  sin_familia:       { color: 'yellow', texto: 'factura y NO la vigilamos' },
  no_soportada:      { color: 'red',    texto: 'tienda no incorporada' },
}

function Aviso({ nivel = 'warn', children }) {
  const c = nivel === 'warn' ? 'warn' : 'error'
  return (
    <div style={{
      marginBottom: 'var(--space-3)', padding: '10px 12px',
      borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', lineHeight: 1.55,
      color: `var(--color-${c})`, background: `var(--color-${c}-soft)`,
      border: `1px solid var(--color-${c}-line)`,
    }}>{children}</div>
  )
}

export function TiendasDelTenant({ clienteId, accionCenso, accionSonda }) {
  // La pestaña y el rótulo del botón NO se escriben aquí: los trae la ficha. La
  // versión anterior los tenía a mano en tres textos y los tres mandaban a la
  // pestaña equivocada desde el 04-09-2026. Si no ha cargado, la frase se omite.
  const rutaCenso = rutaDeAccion(accionCenso)
  const rutaSonda = rutaDeAccion(accionSonda)
  const { data, isLoading } = useTiendas(clienteId)
  const nombreDeTienda = useNombreDeTienda()
  const actualizar = useUpdateFamilia(clienteId)
  const [error, setError] = useState(null)

  if (isLoading) {
    return <div style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>Cargando…</div>
  }

  const tiendas = Array.isArray(data?.tiendas) ? data.tiendas : []
  const censo = data?.censo
  const tot = data?.totales
  const ventana = data?.ventana

  async function alternar(fam) {
    setError(null)
    try {
      await actualizar.mutateAsync({ famId: fam.id, data: { activo: !fam.activo } })
    } catch (e) {
      // Encender consume cupo del tier: si no cabe, el backend responde 4xx y hay
      // que DECIRLO. Un botón que no hace nada es peor que uno que falla.
      setError(e?.response?.data?.detail || 'No se pudo cambiar la tienda.')
    }
  }

  const cols = [
    { key: 'tienda', label: 'Tienda',
      render: (v) => {
        const nombre = nombreDeTienda(v)
        return (
          <div>
            <div style={{ fontFamily: 'var(--font-ui)' }}>{nombre || v}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                          color: 'var(--color-text-3)' }}>{v}</div>
          </div>
        )
      } },
    { key: 'monto', label: 'Facturado', align: 'right', nowrap: true,
      render: (v, f) => (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'var(--font-mono)' }}>{clp(v)}</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
            {f.n_convenios} convenio{f.n_convenios === 1 ? '' : 's'}
          </div>
        </div>
      ) },
    { key: 'ocs', label: 'OCs', align: 'right', nowrap: true,
      render: v => <span style={{ fontFamily: 'var(--font-mono)' }}>
        {(v ?? 0).toLocaleString('es-CL')}</span> },
    { key: 'estado', label: 'Estado', width: '190px',
      render: (v, f) => {
        const e = ESTADO[v] || { color: 'gray', texto: v }
        return (
          <div>
            <Badge color={e.color} dot>{e.texto}</Badge>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                          marginTop: 'var(--space-05)' }}>
              {f.familia
                ? `${ORIGEN_TEXTO[f.familia.origen] || f.familia.origen} · ${(f.familia.n_items ?? 0).toLocaleString('es-CL')} ítems`
                : 'sin familia en el pipeline'}
            </div>
          </div>
        )
      } },
    { key: 'familia', label: '', width: '110px',
      render: (fam) => (fam
        ? <Button variant="secondary" size="sm" disabled={actualizar.isPending}
                  onClick={() => alternar(fam)}>
            {fam.activo ? 'Apagar' : 'Encender'}
          </Button>
        : <span style={{ color: 'var(--color-text-3)' }}>—</span>) },
  ]

  const activas = tiendas.filter(t => t.familia?.activo).map(t => t.tienda)

  return (
    <div>
      {/* Lo primero, porque decide cómo se leen TODAS las cifras de abajo. */}
      {censo?.nunca_corrido && (
        <Aviso>
          El censo de convenios no se ha corrido nunca en este tenant, así que
          <b> no hay dinero medido por tienda</b>. No es que no facture: es que no
          se ha preguntado.{rutaCenso ? ` Se lanza desde ${rutaCenso}.` : ''}
        </Aviso>
      )}
      {censo && !censo.nunca_corrido && !censo.completo && (
        <Aviso>
          El censo va a medias: quedan <b>{censo.ocs_pendientes} órdenes</b> por
          resolver ({clp(censo.monto_pendiente)}). Las cifras de abajo son un
          <b> mínimo</b>, no el total.{rutaCenso ? ` Se completa desde ${rutaCenso}.` : ''}
        </Aviso>
      )}
      {error && <Aviso nivel="error">{error}</Aviso>}

      <div style={{ marginBottom: 'var(--space-3)', fontSize: 'var(--text-sm)',
                    lineHeight: 1.6, color: 'var(--color-text-2)' }}>
        {tiendas.length
          ? <>El próximo run sondeará <b>{activas.length} tienda(s)</b>
              {activas.length ? <>: <span style={{ fontFamily: 'var(--font-mono)' }}>{activas.join(' · ')}</span></> : null}.
              {' '}<Pista etiqueta="coste del sondeo" contenido={
                'Cada tienda encendida multiplica el trabajo del run: se sondea entera, '
                + 'una por una. Apagar las que no facturan acorta la vuelta.'} /></>
          : <>Sin tiendas detectadas todavía. Se pueblan en el paso de detección de
             familias del pipeline, y mientras tanto <b>el pipeline usa el convenio
             del alta</b>: vacío aquí no significa que no sondee nada.</>}
        {ventana && (
          <div style={{ marginTop: 'var(--space-1)', color: 'var(--color-text-3)',
                        fontSize: 'var(--text-xs)' }}>
            {/* La pista envuelve el propio rótulo en vez de colgar un ⓘ al lado:
                una marca menos en pantalla, y el sitio donde uno pregunta es
                justo el dato del que duda. */}
            <Pista etiqueta="ventana medida" contenido={
              `Son los ${ventana.dias} días que trajo el scraper, no un periodo `
              + 'elegido. Todas las cifras de abajo se refieren a esa ventana: si el '
              + 'scraper trae más historial, cambian.'}>
              <span style={{ fontFamily: 'var(--font-mono)' }}>
                {ventana.ocs.toLocaleString('es-CL')} órdenes · {ventana.desde} → {ventana.hasta}
              </span>
            </Pista>
          </div>
        )}
      </div>

      <Table columns={cols} data={tiendas} claveFila={t => t.tienda}
             emptyMessage="Sin tiendas detectadas" />

      {/* Un total repartido, dibujado. Hasta el 02-09 esto eran cuatro frases
          con importes dentro, y el «facturación propia total» al final se leía
          como si los otros lo cubrieran. No lo cubrían: faltaba un 11,8 % que
          ninguna línea explicaba. En prosa nadie suma con la vista; en una
          barra, el hueco es un tramo rojo el primer día.

          Los siete cubos NO son adorno: son exactamente los siete destinos que
          `agregar_censo` distingue, y `BarraTramos` grita si no suman. */}
      {tot && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <BarraTramos
            total={tot.monto_total_propio}
            rotuloTotal="facturación propia en la ventana medida"
            tramos={[
              { clave: 'vigilado', etiqueta: 'Vigilado', valor: tot.monto_vigilado,
                color: 'var(--color-ok)' },
              { clave: 'no-vigiladas', etiqueta: 'En tiendas que no vigilamos',
                valor: tot.monto_en_tiendas_no_vigiladas, color: 'var(--color-warn)',
                pista: 'Factura ahí y no lo miramos. Es cobertura que se está '
                     + 'perdiendo: se gana encendiendo esa tienda, o incorporándola '
                     + 'al pipeline si aún no está.' },
              { clave: 'sin-tienda-conocida', etiqueta: 'Ninguna tienda conocida',
                valor: tot.monto_sin_tienda_conocida, color: 'var(--color-error)',
                pista: 'Convenios que SÍ se sondearon y que ninguna tienda del portal '
                     + 'sirve. Esto no se arregla encendiendo nada: haría falta '
                     + 'incorporar una tienda nueva.' },
              { clave: 'sin-sondear', etiqueta: 'Aún sin sondear',
                // Rayado, y el matiz importa: el IMPORTE sí está medido —por eso
                // la leyenda escribe la cifra—, lo que no se sabe es a qué tienda
                // va. La trama dice «no sabemos dónde cae esto», no «no hay».
                valor: tot.monto_sin_sondear, trama: true,
                // ⚠️ Esto lo resuelve la SONDA, no el censo (Manuel, 20-10-2026).
                // Este globo citaba «Censar convenios» y mandaba a quien lo leyera
                // a pulsar el botón que NO lo arregla: el censo es justo quien
                // DEJA estos convenios en `sin_mapear`, y la sonda es la que les
                // busca tienda. Coló porque el cubo de al lado —«Pendiente de
                // preguntar»— sí es del censo, así que la frase no chirriaba.
                pista: 'Convenios que todavía no se han sondeado: NO se sabe de qué '
                     + 'tienda son. No es dinero perdido, es dinero sin medir.'
                     + (rutaSonda ? ` Se resuelve desde ${rutaSonda}.` : '') },
              { clave: 'pendiente', etiqueta: 'Pendiente de preguntar',
                valor: tot.monto_pendiente_de_preguntar, trama: true,
                pista: 'Órdenes que el censo aún no ha resuelto contra la API pública. '
                     + 'Mientras esto no sea cero, todos los cubos de arriba son un '
                     + 'mínimo, no el total.' },
              { clave: 'sin-marco', etiqueta: 'Fuera de convenio marco',
                valor: tot.monto_sin_marco, color: 'var(--color-text-3)',
                pista: 'Órdenes que no vienen de ningún convenio: Compra Ágil, Trato '
                     + 'Directo, licitaciones propias. No es un fallo, y no se puede '
                     + 'vigilar desde el portal — pero es facturación suya.' },
              { clave: 'otro-marco', etiqueta: 'Marco que no es convenio marco',
                valor: tot.monto_marco_que_no_es_convenio, color: 'var(--color-text-3)',
                pista: 'Declara una licitación, pero no es un convenio marco (por '
                     + 'ejemplo una licitación pública puntual). Tampoco es vigilable '
                     + 'desde el portal.' },
            ]} />
        </div>
      )}
    </div>
  )
}
